import React, { useEffect, useState } from "react";
import useEventsData from "@/src/useData/useEventsData";
import S from "./AllEvents.styled";
import {
  LoadMoreButton,
  Button,
  Icon,
  TextButton,
  Wysiwyg,
} from "@/styles/components";
import Arrow from "@/assets/arrow.svg";
import { IWPImage, IWPLink } from "@/src/types";
import { Flex, MainInner } from "@/styles/global";
import { H3 } from "@/styles/typography";
import { motion } from "framer-motion";

type Events = {
  title: string;
  uri: string;
  date: string;
  eventsInfo: {
    eventDate: string;
    location: string;
    featuredImage: IWPImage;
    staffOnSite: string;
    content: string;
  };
};

export interface AllEventsProps {
  link: IWPLink;
  blockId: string;
}

const AllEvents: React.FC<AllEventsProps> = ({ link, blockId = "" }) => {
  const [paginatedEvents, setPaginatedEvents] = useState<Events[]>([]);
  const events = useEventsData();
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const newEvents = events.slice(0, (currentPage + 1) * 10);
    setPaginatedEvents(newEvents);
  }, [currentPage, events]);

  const renderCard = (events: Events, key: number) => {
    const {
      title,
      uri,
      eventsInfo: { location, eventDate, content },
    } = events;
    return (
      <motion.div
        key={key}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <S.Card to={uri}>
          <S.CardTextWrapper>
            <Flex $column $gap={8}>
              <Flex $gap={16}>
                {eventDate && <S.CardDate>{eventDate}</S.CardDate>}
                {location && <S.CardDate>{location}</S.CardDate>}
                {!eventDate && !location && <S.CardDate>Event</S.CardDate>}
              </Flex>
              <H3>{title}</H3>
            </Flex>
            <S.ContentWrapper>
              <Wysiwyg size={16} color="textBlack" html={content} />
              <Button label="Read more" url={uri} />
            </S.ContentWrapper>
            <S.CardReadMoreWrapper>
              <S.CardReadMore>Read more</S.CardReadMore>
              <Arrow />
            </S.CardReadMoreWrapper>
          </S.CardTextWrapper>
        </S.Card>
      </motion.div>
    );
  };

  return (
    <S.Section id={blockId}>
      <MainInner>
        {link && (
          <TextButton
            wpLink={link}
            color="black"
            underlined
            left={
              <Icon
                src="ArrowLeftOutline"
                width={15}
                margin="0 16px 0 0"
                color="red"
              />
            }
          />
        )}
        <S.Container>
          {paginatedEvents.map((eventItem, i) => renderCard(eventItem, i))}
        </S.Container>
        {paginatedEvents.length < events.length && (
          <Flex $center $width="100%" $margin="24px 0">
            <LoadMoreButton
              label="Load more"
              onClick={() => setCurrentPage((prevPage) => prevPage + 1)}
            />
          </Flex>
        )}
      </MainInner>
    </S.Section>
  );
};

export default AllEvents;
